/**
 * Public Birthday Widget Page
 *
 * Public-facing display of student birthdays.
 * No authentication required - designed for embedding or public viewing.
 * Applies studio theme (colors, fonts) for branded experience.
 */

import React, { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useGetPublicBirthdayWidgetQuery } from '../../api/birthdayWidgetApi';
import Watermark from '../../components/common/Watermark';
import { shouldShowWatermark } from '../../utils/watermark';

const PublicBirthdayWidgetPage: React.FC = () => {
  const { studioSlug } = useParams<{ studioSlug: string }>();
  const contentRef = useRef<HTMLDivElement>(null);

  const {
    data: birthdayData,
    isLoading,
    error,
  } = useGetPublicBirthdayWidgetQuery(studioSlug || '', {
    skip: !studioSlug,
  });

  // Extract theme colors (must be before conditional returns)
  const { config, students, theme } = birthdayData || {};
  const fontFamily =
    (theme?.tokens?.typography as any)?.fontFamily?.body || 'Arial, sans-serif';
  const textColor = (theme?.tokens?.colors as any)?.text?.primary || '#000000';
  const backgroundColor =
    (theme?.tokens?.colors as any)?.background || '#ffffff';

  // Set document body and html background color to match theme
  useEffect(() => {
    const originalBodyBg = document.body.style.backgroundColor;
    const originalHtmlBg = document.documentElement.style.backgroundColor;

    document.body.style.backgroundColor = backgroundColor;
    document.documentElement.style.backgroundColor = backgroundColor;

    return () => {
      document.body.style.backgroundColor = originalBodyBg;
      document.documentElement.style.backgroundColor = originalHtmlBg;
    };
  }, [backgroundColor]);

  // Post height to parent window for iframe resizing
  useEffect(() => {
    if (!birthdayData) {
      return undefined;
    }

    const postHeight = () => {
      if (contentRef.current && window.parent) {
        const height = contentRef.current.offsetHeight;
        window.parent.postMessage(
          {
            type: 'birthday-widget-height',
            height: height + 20, // Add some padding
          },
          '*'
        );
      }
    };

    // Post height after content loads
    postHeight();

    // Use ResizeObserver to detect content changes
    const resizeObserver = new ResizeObserver(() => {
      postHeight();
    });

    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [birthdayData]);

  // Loading state
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress size={50} />
      </Box>
    );
  }

  // Error state
  if (error || !birthdayData) {
    return (
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <Alert severity="error">
          Unable to load birthday widget. Please check the URL and try again.
        </Alert>
      </Container>
    );
  }

  return (
    <Box
      ref={contentRef}
      sx={{
        width: '100%',
        backgroundColor,
        fontFamily,
        py: 1,
        px: 1,
      }}
    >
      <Box sx={{ width: '100%' }}>
        {/* Centered Title */}
        <Box mb={1} textAlign="center">
          <Typography
            variant="h6"
            component="h1"
            sx={{
              fontFamily,
              color: textColor,
              fontWeight: 'bold',
            }}
          >
            {config?.greeting_message || 'Happy Birthday!'}
          </Typography>
        </Box>

        {/* Comma-Separated Student Names */}
        {!students || students.length === 0 ? (
          <Typography
            variant="body2"
            sx={{
              fontFamily,
              color: textColor,
              textAlign: 'center',
              opacity: 0.7,
            }}
          >
            No birthdays to celebrate at this time.
          </Typography>
        ) : (
          <Typography
            variant="body1"
            sx={{
              fontFamily,
              color: textColor,
              textAlign: 'center',
              lineHeight: 1.6,
            }}
          >
            {students.map(student => student.display_name).join(', ')}
          </Typography>
        )}

        {/* Watermark (for free tier only) */}
        <Watermark
          textColor={textColor}
          shouldDisplay={shouldShowWatermark(
            birthdayData.studio.owner_plan_slug
          )}
        />
      </Box>
    </Box>
  );
};

export default PublicBirthdayWidgetPage;
